import React, { useState, useEffect, useContext, useRef } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  Button,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  Phone as PhoneIcon,
  PhoneDisabled as HangupIcon,
  Settings as SettingsIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon
} from "@material-ui/icons";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import { es } from "date-fns/locale";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  section: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
  callButton: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    fontSize: "1.1rem",
  },
  hangupButton: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    fontSize: "1.1rem",
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
    flexDirection: "column",
  },
  callControls: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    justifyContent: "center",
  },
  callStatus: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.info.light,
    borderRadius: theme.shape.borderRadius,
    textAlign: "center",
  },
}));

const LiveCalls = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [twilioConfigured, setTwilioConfigured] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("+52");
  const [callHistory, setCallHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Lista completa de códigos de país
  const countryCodes = [
    // América
    { code: "+1", flag: "🇺🇸", name: "Estados Unidos/Canadá" },
    { code: "+52", flag: "🇲🇽", name: "México" },
    { code: "+54", flag: "🇦🇷", name: "Argentina" },
    { code: "+55", flag: "🇧🇷", name: "Brasil" },
    { code: "+56", flag: "🇨🇱", name: "Chile" },
    { code: "+57", flag: "🇨🇴", name: "Colombia" },
    { code: "+58", flag: "🇻🇪", name: "Venezuela" },
    { code: "+51", flag: "🇵🇪", name: "Perú" },
    { code: "+593", flag: "🇪🇨", name: "Ecuador" },
    { code: "+591", flag: "🇧🇴", name: "Bolivia" },
    { code: "+595", flag: "🇵🇾", name: "Paraguay" },
    { code: "+598", flag: "🇺🇾", name: "Uruguay" },
    { code: "+507", flag: "🇵🇦", name: "Panamá" },
    { code: "+506", flag: "🇨🇷", name: "Costa Rica" },
    { code: "+503", flag: "🇸🇻", name: "El Salvador" },
    { code: "+502", flag: "🇬🇹", name: "Guatemala" },
    { code: "+504", flag: "🇭🇳", name: "Honduras" },
    { code: "+505", flag: "🇳🇮", name: "Nicaragua" },
    { code: "+53", flag: "🇨🇺", name: "Cuba" },
    { code: "+1-809", flag: "🇩🇴", name: "República Dominicana" },
    { code: "+1-787", flag: "🇵🇷", name: "Puerto Rico" },
    { code: "+1-876", flag: "🇯🇲", name: "Jamaica" },
    { code: "+509", flag: "🇭🇹", name: "Haití" },
    
    // Europa
    { code: "+34", flag: "🇪🇸", name: "España" },
    { code: "+44", flag: "🇬🇧", name: "Reino Unido" },
    { code: "+33", flag: "🇫🇷", name: "Francia" },
    { code: "+49", flag: "🇩🇪", name: "Alemania" },
    { code: "+39", flag: "🇮🇹", name: "Italia" },
    { code: "+351", flag: "🇵🇹", name: "Portugal" },
    { code: "+31", flag: "🇳🇱", name: "Países Bajos" },
    { code: "+32", flag: "🇧🇪", name: "Bélgica" },
    { code: "+41", flag: "🇨🇭", name: "Suiza" },
    { code: "+43", flag: "🇦🇹", name: "Austria" },
    { code: "+45", flag: "🇩🇰", name: "Dinamarca" },
    { code: "+46", flag: "🇸🇪", name: "Suecia" },
    { code: "+47", flag: "🇳🇴", name: "Noruega" },
    { code: "+358", flag: "🇫🇮", name: "Finlandia" },
    { code: "+353", flag: "🇮🇪", name: "Irlanda" },
    { code: "+30", flag: "🇬🇷", name: "Grecia" },
    { code: "+48", flag: "🇵🇱", name: "Polonia" },
    { code: "+420", flag: "🇨🇿", name: "República Checa" },
    { code: "+36", flag: "🇭🇺", name: "Hungría" },
    { code: "+40", flag: "🇷🇴", name: "Rumania" },
    { code: "+7", flag: "🇷🇺", name: "Rusia" },
    { code: "+380", flag: "🇺🇦", name: "Ucrania" },
    { code: "+90", flag: "🇹🇷", name: "Turquía" },
    
    // Medio Oriente
    { code: "+972", flag: "🇮🇱", name: "Israel" },
    { code: "+971", flag: "🇦🇪", name: "Emiratos Árabes" },
    { code: "+966", flag: "🇸🇦", name: "Arabia Saudita" },
    { code: "+20", flag: "🇪🇬", name: "Egipto" },
    { code: "+962", flag: "🇯🇴", name: "Jordania" },
    { code: "+961", flag: "🇱🇧", name: "Líbano" },
    { code: "+964", flag: "🇮🇶", name: "Irak" },
    { code: "+98", flag: "🇮🇷", name: "Irán" },
    { code: "+974", flag: "🇶🇦", name: "Qatar" },
    { code: "+965", flag: "🇰🇼", name: "Kuwait" },
    { code: "+968", flag: "🇴🇲", name: "Omán" },
    { code: "+973", flag: "🇧🇭", name: "Baréin" },
    
    // África
    { code: "+27", flag: "🇿🇦", name: "Sudáfrica" },
    { code: "+234", flag: "🇳🇬", name: "Nigeria" },
    { code: "+254", flag: "🇰🇪", name: "Kenia" },
    { code: "+212", flag: "🇲🇦", name: "Marruecos" },
    { code: "+213", flag: "🇩🇿", name: "Argelia" },
    { code: "+216", flag: "🇹🇳", name: "Túnez" },
    { code: "+233", flag: "🇬🇭", name: "Ghana" },
    { code: "+255", flag: "🇹🇿", name: "Tanzania" },
    { code: "+256", flag: "🇺🇬", name: "Uganda" },
    { code: "+251", flag: "🇪🇹", name: "Etiopía" },
    
    // Asia
    { code: "+91", flag: "🇮🇳", name: "India" },
    { code: "+92", flag: "🇵🇰", name: "Pakistán" },
    { code: "+880", flag: "🇧🇩", name: "Bangladesh" },
    { code: "+86", flag: "🇨🇳", name: "China" },
    { code: "+81", flag: "🇯🇵", name: "Japón" },
    { code: "+82", flag: "🇰🇷", name: "Corea del Sur" },
    { code: "+84", flag: "🇻🇳", name: "Vietnam" },
    { code: "+66", flag: "🇹🇭", name: "Tailandia" },
    { code: "+65", flag: "🇸🇬", name: "Singapur" },
    { code: "+60", flag: "🇲🇾", name: "Malasia" },
    { code: "+62", flag: "🇮🇩", name: "Indonesia" },
    { code: "+63", flag: "🇵🇭", name: "Filipinas" },
    { code: "+94", flag: "🇱🇰", name: "Sri Lanka" },
    { code: "+95", flag: "🇲🇲", name: "Myanmar" },
    { code: "+855", flag: "🇰🇭", name: "Camboya" },
    { code: "+856", flag: "🇱🇦", name: "Laos" },
    { code: "+977", flag: "🇳🇵", name: "Nepal" },
    
    // Oceanía
    { code: "+61", flag: "🇦🇺", name: "Australia" },
    { code: "+64", flag: "🇳🇿", name: "Nueva Zelanda" },
    { code: "+679", flag: "🇫🇯", name: "Fiyi" },
    { code: "+675", flag: "🇵🇬", name: "Papúa Nueva Guinea" },
  ];
  
  // Twilio Device states
  const [deviceReady, setDeviceReady] = useState(false);
  const [initializingDevice, setInitializingDevice] = useState(false);
  const [callActive, setCallActive] = useState(false);
  const [callStatus, setCallStatus] = useState("");
  const [muted, setMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  
  const deviceRef = useRef(null);
  const connectionRef = useRef(null);
  const durationIntervalRef = useRef(null);

  useEffect(() => {
    checkTwilioConfig();
    loadCallHistory();
    
    return () => {
      // Cleanup on unmount (SDK v2)
      if (connectionRef.current) {
        connectionRef.current.disconnect();
      }
      if (deviceRef.current) {
        deviceRef.current.unregister();
        deviceRef.current.destroy();
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, []);

  const checkTwilioConfig = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/api/twilio/live-calls/config/status", {
        params: { companyId: user.companyId }
      });
      setTwilioConfigured(data.configured);
    } catch (error) {
      console.error("Live Calls not configured:", error);
      setTwilioConfigured(false);
    } finally {
      setLoading(false);
    }
  };

  const loadCallHistory = async () => {
    try {
      setLoadingHistory(true);
      const { data } = await api.get("/api/twilio/live-calls/history", {
        params: {
          companyId: user.companyId,
          limit: 50,
          offset: 0
        }
      });
      setCallHistory(data.calls || []);
    } catch (error) {
      console.error("Error loading call history:", error);
      setCallHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const initializeTwilioDevice = async () => {
    if (deviceRef.current || initializingDevice) {
      return;
    }

    try {
      setInitializingDevice(true);
      setCallStatus("Inicializando dispositivo...");

      // Get access token
      console.log("Solicitando token de acceso...");
      const { data } = await api.post("/api/twilio/live-calls/token", {
        companyId: user.companyId
      });
      console.log("Token recibido:", data);

      // Load Twilio Device SDK v2
      const { Device } = await import("@twilio/voice-sdk");

      // Create device with SDK v2
      console.log("Creando dispositivo Twilio v2...");
      const device = new Device(data.token, {
        codecPreferences: ["opus", "pcmu"],
        logLevel: 1, // debug level
        edge: "ashburn" // Use edge instead of region
      });

      // Device event listeners (SDK v2)
      device.on("registered", () => {
        console.log("✅ Twilio Device registered");
        setDeviceReady(true);
        setCallStatus("Dispositivo listo");
        setInitializingDevice(false);
        toast.success("Dispositivo de llamadas listo");
      });

      device.on("error", (error) => {
        console.error("❌ Twilio Device error:", error);
        setCallStatus(`Error: ${error.message}`);
        setInitializingDevice(false);
        setDeviceReady(false);
        toast.error(`Error del dispositivo: ${error.message}`);
      });

      device.on("unregistered", () => {
        console.warn("⚠️ Twilio Device unregistered");
        setDeviceReady(false);
        setCallStatus("Dispositivo desconectado");
      });

      device.on("tokenWillExpire", async () => {
        console.log("🔄 Token will expire, refreshing...");
        try {
          const { data: newData } = await api.post("/api/twilio/live-calls/token", {
            companyId: user.companyId
          });
          device.updateToken(newData.token);
        } catch (error) {
          console.error("Error refreshing token:", error);
        }
      });

      device.on("incoming", (call) => {
        console.log("📥 Incoming call (should not happen for outbound-only)");
        call.reject();
      });

      deviceRef.current = device;

      // Register device for incoming calls (required in v2)
      console.log("Registrando dispositivo...");
      await device.register();

    } catch (error) {
      console.error("❌ Error initializing Twilio Device:", error);
      setCallStatus(`Error: ${error.message}`);
      toast.error("Error al inicializar dispositivo de llamadas");
      setInitializingDevice(false);
      setDeviceReady(false);
    }
  };

  const handleCall = async () => {
    if (!deviceReady) {
      await initializeTwilioDevice();
      // Wait a bit for device to be ready
      setTimeout(() => {
        if (deviceRef.current && deviceRef.current.state === "registered") {
          makeCall();
        } else {
          toast.warning("Esperando a que el dispositivo esté listo...");
        }
      }, 1000);
      return;
    }

    await makeCall();
  };

  const makeCall = async () => {
    if (!deviceRef.current) {
      toast.error("Dispositivo no inicializado");
      return;
    }

    const fullNumber = `${countryCode}${phoneNumber}`;
    
    try {
      setCallStatus(`Llamando a ${fullNumber}...`);
      
      // SDK v2: device.connect returns a Promise
      const call = await deviceRef.current.connect({
        params: { To: fullNumber }
      });
      
      // Call event listeners (SDK v2)
      call.on("ringing", () => {
        setCallStatus("Timbrando...");
      });

      call.on("accept", () => {
        setCallStatus("Llamada aceptada");
        setCallActive(true);
        startCallDurationTimer();
      });

      call.on("disconnect", () => {
        console.log("📴 Call disconnected");
        setCallActive(false);
        setCallStatus("Llamada finalizada");
        stopCallDurationTimer();
        connectionRef.current = null;
        loadCallHistory();
      });

      call.on("error", (error) => {
        console.error("Call error:", error);
        toast.error(`Error en la llamada: ${error.message}`);
        setCallStatus("Error en la llamada");
      });

      connectionRef.current = call;
    } catch (error) {
      console.error("Error making call:", error);
      toast.error("Error al realizar la llamada");
      setCallStatus("Error al llamar");
    }
  };

  const handleHangup = () => {
    if (connectionRef.current) {
      connectionRef.current.disconnect();
    }
  };

  const toggleMute = () => {
    if (connectionRef.current) {
      // SDK v2: mute() takes a boolean parameter
      connectionRef.current.mute(!muted);
      setMuted(!muted);
      toast.info(muted ? "Micrófono activado" : "Micrófono silenciado");
    }
  };

  const startCallDurationTimer = () => {
    setCallDuration(0);
    durationIntervalRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
  };

  const stopCallDurationTimer = () => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    setCallDuration(0);
  };

  const formatCallDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "dd/MM/yyyy HH:mm", { locale: es });
    } catch (error) {
      return "-";
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getStatusText = (status) => {
    const statusMap = {
      queued: "En cola",
      ringing: "Timbrando",
      "in-progress": "En progreso",
      completed: "Completada",
      failed: "Fallida",
      busy: "Ocupado",
      "no-answer": "Sin respuesta",
      canceled: "Cancelada"
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      completed: "primary",
      "in-progress": "secondary",
      failed: "error",
      busy: "warning",
      "no-answer": "default",
    };
    return colorMap[status] || "default";
  };

  if (loading) {
    return (
      <MainContainer>
        <MainHeader>
          <Title>Llamadas en Vivo</Title>
        </MainHeader>
        <Paper className={classes.mainPaper} variant="outlined">
          <Box className={classes.loadingContainer}>
            <CircularProgress />
            <Typography variant="body1" style={{ marginTop: 16 }}>
              Cargando...
            </Typography>
          </Box>
        </Paper>
      </MainContainer>
    );
  }

  if (!twilioConfigured) {
    return (
      <MainContainer>
        <MainHeader>
          <Title>Llamadas en Vivo</Title>
        </MainHeader>
        <Paper className={classes.mainPaper} variant="outlined">
          <Alert severity="warning">
            <Typography variant="h6" gutterBottom>
              Configuración de Llamadas en Vivo Requerida
            </Typography>
            <Typography variant="body2" paragraph>
              La configuración de Llamadas en Vivo no está completa. Por favor, configura tus credenciales de Twilio antes de realizar llamadas.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SettingsIcon />}
              onClick={() => history.push("/live-calls-config")}
            >
              Ir a Configuración
            </Button>
          </Alert>
        </Paper>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>Llamadas en Vivo</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<SettingsIcon />}
            onClick={() => history.push("/live-calls-config")}
          >
            Configuración
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Grid container spacing={3}>
          {/* Call Section */}
          <Grid item xs={12} md={6}>
            <Paper className={classes.section}>
              <Typography variant="h6" gutterBottom>
                Realizar Llamada
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Código</InputLabel>
                    <Select
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      disabled={callActive}
                      label="Código"
                    >
                      {countryCodes.map((country) => (
                        <MenuItem key={country.code} value={country.code}>
                          {country.flag} {country.name} {country.code}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    label="Número de Teléfono"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="Ej: 5551234567"
                    variant="outlined"
                    disabled={callActive}
                  />
                </Grid>
              </Grid>

              {!callActive ? (
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={initializingDevice ? <CircularProgress size={20} color="inherit" /> : <PhoneIcon />}
                  onClick={handleCall}
                  className={classes.callButton}
                  disabled={!phoneNumber || initializingDevice}
                >
                  {initializingDevice ? "Inicializando..." : "Llamar"}
                </Button>
              ) : (
                <>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<HangupIcon />}
                    onClick={handleHangup}
                    className={classes.hangupButton}
                  >
                    Colgar
                  </Button>

                  <Box className={classes.callControls}>
                    <IconButton
                      color={muted ? "secondary" : "default"}
                      onClick={toggleMute}
                      title={muted ? "Activar micrófono" : "Silenciar micrófono"}
                    >
                      {muted ? <MicOffIcon /> : <MicIcon />}
                    </IconButton>
                  </Box>
                </>
              )}

              {callStatus && (
                <Box className={classes.callStatus}>
                  <Typography variant="body1">
                    {callStatus}
                  </Typography>
                  {callActive && (
                    <Typography variant="h5" style={{ marginTop: 8 }}>
                      {formatCallDuration(callDuration)}
                    </Typography>
                  )}
                </Box>
              )}

              {!deviceReady && !initializingDevice && (
                <Alert severity="info" style={{ marginTop: 16 }}>
                  <Typography variant="body2">
                    Haz clic en "Llamar" para inicializar el dispositivo de llamadas.
                    Se te pedirá permiso para usar el micrófono.
                  </Typography>
                </Alert>
              )}
            </Paper>
          </Grid>

          {/* Call History Section */}
          <Grid item xs={12} md={6}>
            <Paper className={classes.section}>
              <Typography variant="h6" gutterBottom>
                Historial de Llamadas
              </Typography>

              {loadingHistory ? (
                <Box className={classes.loadingContainer}>
                  <CircularProgress size={30} />
                </Box>
              ) : callHistory.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Número</TableCell>
                        <TableCell>Duración</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell>Fecha</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {callHistory.slice(0, 10).map((call) => (
                        <TableRow key={call.id}>
                          <TableCell>{call.phoneNumber}</TableCell>
                          <TableCell>{formatDuration(call.duration)}</TableCell>
                          <TableCell>
                            <Chip
                              label={getStatusText(call.status)}
                              color={getStatusColor(call.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{formatDate(call.startTime)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="textSecondary" align="center">
                  No hay llamadas registradas
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </MainContainer>
  );
};

export default LiveCalls;
